import { Bed, Coffee, Dumbbell, LucideIcon, Pill, Wine, X } from "lucide-react";
import { ToggleGroup, ToggleGroupItem } from "../../shadcn/toggle-group";
import { Button } from "../../shadcn/button";
import { cn } from "../../../lib/cn";
import { ActivityType, NightInput } from "./api";
import { addMinutes, toMinutes } from "./nightClock";
import { TimePicker } from "./TimePicker";

export type Activity = NightInput["activities"][number];

/** C1 caps a night at 20 activity intervals. */
const MAX_ACTIVITIES = 20;
/** C2 refuses an interval longer than 12 hours. */
const MAX_MINUTES = 720;

// R3 keeps the stored type `alchohol` (the EMR timeline reads it).
export const ACTIVITIES: { type: ActivityType; label: string; icon: LucideIcon; start: string; minutes: number }[] = [
  { type: "exercise", label: "Exercise", icon: Dumbbell, start: "17:30", minutes: 45 },
  { type: "nap", label: "Nap", icon: Bed, start: "14:00", minutes: 30 },
  { type: "caffeine", label: "Caffeine", icon: Coffee, start: "09:00", minutes: 15 },
  { type: "alchohol", label: "Alcohol", icon: Wine, start: "20:00", minutes: 60 },
  { type: "drugs", label: "Medication", icon: Pill, start: "22:00", minutes: 15 },
];

const durationOf = (a: Activity) => (toMinutes(a.end) - toMinutes(a.start) + 1440) % 1440;

export type ActivityTilesProps = { value: Activity[]; onChange(next: Activity[]): void };

/** Tiles toggle a type; each type reveals its time-only ranges (D3). */
export function ActivityTiles({ value, onChange }: ActivityTilesProps) {
  const full = value.length >= MAX_ACTIVITIES;
  const on = ACTIVITIES.filter((a) => value.some((v) => v.type === a.type)).map((a) => a.type);

  const toggle = (next: string[]) => {
    const kept = value.filter((v) => next.includes(v.type));
    const added = ACTIVITIES.filter((a) => next.includes(a.type) && !on.includes(a.type)).map((a) => ({
      type: a.type,
      start: a.start,
      end: addMinutes(a.start, a.minutes),
    }));
    onChange([...kept, ...added]);
  };

  // Rows grouped by type in tile order; `index` points into `value`, `k` is the row within its type.
  const rows = ACTIVITIES.flatMap((a) => {
    const own = value.map((v, index) => ({ v, index })).filter(({ v }) => v.type === a.type);
    return own.map(({ v, index }, k) => ({ a, v, index, k, count: own.length }));
  });

  const replace = (index: number, next: Activity) => onChange(value.map((v, i) => (i === index ? next : v)));

  return (
    <div>
      <ToggleGroup
        type="multiple"
        variant="tile"
        aria-label="Activities"
        className="mt-3 grid grid-cols-3 gap-2 min-[1100px]:grid-cols-5"
        value={on}
        onValueChange={toggle}
      >
        {ACTIVITIES.map(({ type, label, icon: Icon }) => (
          <ToggleGroupItem key={type} value={type} disabled={full && !on.includes(type)} className="group text-[13px]">
            <Icon
              size={18}
              aria-hidden
              className="text-neutral-500 transition-colors duration-150 ease-soft group-data-[state=on]:text-brand"
            />
            {label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
      <div
        className={cn(
          "grid transition-[grid-template-rows,opacity] duration-200 ease-soft motion-reduce:transition-opacity",
          rows.length ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        )}
      >
        <div className="overflow-hidden" aria-hidden={!rows.length}>
          <div className="pt-3">
            {rows.map(({ a, v, index, k, count }, r) => {
              const name = k === 0 ? a.label : `${a.label} ${k + 1}`;
              return (
                <div
                  key={index}
                  className={cn("flex flex-wrap items-center gap-2 py-2", r > 0 && "border-t border-dashed border-neutral-200")}
                >
                  <span className="w-[120px] text-[13px] font-medium">{k === 0 ? a.label : ""}</span>
                  <TimePicker
                    size="sm"
                    label={`${name} start`}
                    value={v.start}
                    onChange={(start) => replace(index, { ...v, start, end: addMinutes(start, durationOf(v)) })}
                  />
                  <span className="text-[13px] text-neutral-400">to</span>
                  <TimePicker size="sm" label={`${name} end`} value={v.end} onChange={(end) => replace(index, { ...v, end })} />
                  {(durationOf(v) > MAX_MINUTES || durationOf(v) === 0) && (
                    <p role="alert" className="w-full text-[13px] text-red-600">
                      {name} must end after it starts and last at most 12 hours.
                    </p>
                  )}
                  {k === count - 1 && value.length < MAX_ACTIVITIES && (
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="px-0"
                      onClick={() => onChange([...value, { type: a.type, start: v.end, end: addMinutes(v.end, a.minutes) }])}
                    >
                      Add time
                    </Button>
                  )}
                  {count > 1 && (
                    <button
                      type="button"
                      aria-label={`Remove ${name}`}
                      className="grid h-7 w-7 place-items-center rounded-md text-neutral-400 transition-colors duration-150 ease-soft hover:bg-neutral-50 hover:text-neutral-950"
                      onClick={() => onChange(value.filter((_, i) => i !== index))}
                    >
                      <X size={14} aria-hidden />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
